import { useForm } from 'react-hook-form';
import { Modal, Button } from '@components/ui';
import { useState, useRef } from 'react';
import { Plus, X, Users, Camera, Trash2 } from 'lucide-react';

/**
 * GunlukModal - Saha raporu oluşturma / düzenleme formu
 */
export default function GunlukModal({ isOpen, onClose, onSubmit, rapor }) {
    const isEdit = !!rapor;
    const [yapilanIsler, setYapilanIsler] = useState(rapor?.yapilanIsler || []);
    const [sorunlar, setSorunlar] = useState(rapor?.sorunlar || []);
    const [yeniIs, setYeniIs] = useState('');
    const [yeniSorun, setYeniSorun] = useState('');
    const [photos, setPhotos] = useState(
        rapor?.photoUrls?.map((url, i) => ({ id: `existing-${i}`, url, name: `foto-${i + 1}` })) || []
    );
    const fileInputRef = useRef(null);

    const { register, handleSubmit, formState: { errors }, reset } = useForm({
        defaultValues: rapor || {
            tarih: new Date().toISOString().split('T')[0],
            personelSayisi: 0,
            ozet: '',
            yarinPlani: '',
        }
    });

    const onFormSubmit = (data) => {
        onSubmit({
            ...data,
            yapilanIsler,
            sorunlar,
            personelSayisi: parseInt(data.personelSayisi),
            kaydeden: 'Ahmet Yılmaz',
            sefId: 'sef-1',
            photoUrls: photos.map(p => p.url),
            photoCount: photos.length,
        });
        reset();
        setYapilanIsler([]);
        setSorunlar([]);
        setPhotos([]);
    };

    const addYapilanIs = () => {
        if (yeniIs.trim()) { setYapilanIsler([...yapilanIsler, yeniIs.trim()]); setYeniIs(''); }
    };
    const removeYapilanIs = (idx) => setYapilanIsler(yapilanIsler.filter((_, i) => i !== idx));
    const addSorun = () => {
        if (yeniSorun.trim()) { setSorunlar([...sorunlar, yeniSorun.trim()]); setYeniSorun(''); }
    };
    const removeSorun = (idx) => setSorunlar(sorunlar.filter((_, i) => i !== idx));

    const handlePhotoSelect = (e) => {
        const files = Array.from(e.target.files);
        const remaining = 10 - photos.length;
        files.slice(0, remaining).forEach(file => {
            const url = URL.createObjectURL(file);
            setPhotos(prev => [...prev, { id: `photo-${Date.now()}-${Math.random()}`, url, name: file.name, file }]);
        });
        e.target.value = '';
    };
    const removePhoto = (id) => setPhotos(prev => prev.filter(p => p.id !== id));

    const inputClass = "w-full px-3 py-2 bg-bg-tertiary border border-border-light rounded-lg text-text-primary focus:ring-2 focus:ring-primary focus:border-transparent text-sm";
    const labelClass = "block text-sm font-medium text-text-secondary mb-1";

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? 'Rapor Düzenle' : 'Günlük Rapor Oluştur'}
            size="lg"
            fullScreenMobile
        >
            <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-5">
                {/* Tarih + Personel */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>Tarih *</label>
                        <input
                            type="date"
                            {...register('tarih', { required: 'Tarih zorunlu' })}
                            className={inputClass}
                        />
                        {errors.tarih && <span className="text-error text-xs">{errors.tarih.message}</span>}
                    </div>
                    <div>
                        <label className={labelClass}>
                            <Users size={14} className="inline mr-1" />Personel Sayısı
                        </label>
                        <input
                            type="number"
                            {...register('personelSayisi')}
                            className={inputClass}
                            placeholder="0"
                        />
                    </div>
                </div>

                {/* Günün Özeti */}
                <div>
                    <label className={labelClass}>Günün Özeti *</label>
                    <textarea
                        {...register('ozet', { required: 'Özet zorunlu' })}
                        className={`${inputClass} min-h-[80px]`}
                        placeholder="Bugün şantiyede neler oldu..."
                    />
                    {errors.ozet && <span className="text-error text-xs">{errors.ozet.message}</span>}
                </div>

                {/* Yapılan İşler */}
                <div>
                    <label className={labelClass}>Yapılan İşler</label>
                    <div className="flex gap-2 mb-2">
                        <input
                            type="text"
                            value={yeniIs}
                            onChange={(e) => setYeniIs(e.target.value)}
                            className={`${inputClass} flex-1`}
                            placeholder="İş ekle ve Enter'a bas..."
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addYapilanIs(); } }}
                        />
                        <Button type="button" variant="secondary" size="sm" onClick={addYapilanIs}>
                            <Plus size={15} />
                        </Button>
                    </div>
                    {yapilanIsler.length > 0 && (
                        <ul className="space-y-1 max-h-32 overflow-y-auto">
                            {yapilanIsler.map((is, idx) => (
                                <li key={idx} className="flex items-center justify-between text-sm bg-success/5 border border-success/20 px-3 py-1.5 rounded-lg">
                                    <span className="text-text-primary flex items-center gap-2">
                                        <span className="text-success text-xs">✓</span> {is}
                                    </span>
                                    <button type="button" onClick={() => removeYapilanIs(idx)} className="text-text-muted hover:text-error ml-2 shrink-0">
                                        <X size={13} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Sorunlar */}
                <div>
                    <label className={labelClass}>Sorunlar / Aksaklıklar</label>
                    <div className="flex gap-2 mb-2">
                        <input
                            type="text"
                            value={yeniSorun}
                            onChange={(e) => setYeniSorun(e.target.value)}
                            className={`${inputClass} flex-1`}
                            placeholder="Sorun ekle ve Enter'a bas..."
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSorun(); } }}
                        />
                        <Button type="button" variant="ghost" size="sm" onClick={addSorun} className="!text-warning border border-warning/30">
                            <Plus size={15} />
                        </Button>
                    </div>
                    {sorunlar.length > 0 && (
                        <ul className="space-y-1 max-h-28 overflow-y-auto">
                            {sorunlar.map((sorun, idx) => (
                                <li key={idx} className="flex items-center justify-between text-sm bg-warning/5 border border-warning/20 px-3 py-1.5 rounded-lg">
                                    <span className="text-text-primary flex items-center gap-2">
                                        <span className="text-warning text-xs">⚠</span> {sorun}
                                    </span>
                                    <button type="button" onClick={() => removeSorun(idx)} className="text-text-muted hover:text-error ml-2 shrink-0">
                                        <X size={13} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Yarın Planı */}
                <div>
                    <label className={labelClass}>Yarın Planı</label>
                    <textarea
                        {...register('yarinPlani')}
                        className={`${inputClass} min-h-[60px]`}
                        placeholder="Yarın ne yapılacak, hangi malzeme gelecek..."
                    />
                </div>

                {/* Saha Fotoğrafları */}
                <div>
                    <label className={labelClass}>
                        <Camera size={14} className="inline mr-1" />
                        Saha Fotoğrafları {photos.length > 0 && <span className="text-primary font-semibold">({photos.length}/10)</span>}
                    </label>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {photos.map(photo => (
                            <div key={photo.id} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-border-light">
                                <img src={photo.url} alt={photo.name} className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => removePhoto(photo.id)}
                                    className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                >
                                    <Trash2 size={16} className="text-white" />
                                </button>
                            </div>
                        ))}
                        {photos.length < 10 && (
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="w-20 h-20 rounded-lg border-2 border-dashed border-border-light flex flex-col items-center justify-center gap-1 text-text-muted hover:border-primary hover:text-primary transition-colors"
                            >
                                <Camera size={20} />
                                <span className="text-xs">Foto Ekle</span>
                            </button>
                        )}
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handlePhotoSelect}
                    />
                    {photos.length === 0 && (
                        <p className="text-xs text-text-muted mt-1">Şantiyeden saha fotoğrafları ekleyebilirsiniz. Maks. 10 fotoğraf.</p>
                    )}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-border-light">
                    <Button type="button" variant="ghost" onClick={onClose}>İptal</Button>
                    <Button type="submit" variant="primary">
                        {isEdit ? 'Güncelle' : 'Rapor Kaydet'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
